import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { notificationsApi } from '../lib/api';
import { toast } from 'sonner';
import {
  Bell,
  FileText,
  GraduationCap,
  AlertTriangle,
  Check,
  CheckCheck,
  Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const NotificationIcon = ({ type }) => {
  switch (type) {
    case 'document_expiry':
      return <FileText className="w-5 h-5 text-amber-600" />;
    case 'training_expiry':
      return <GraduationCap className="w-5 h-5 text-amber-600" />;
    case 'safety_alert':
      return <AlertTriangle className="w-5 h-5 text-rose-600" />;
    default:
      return <Bell className="w-5 h-5 text-primary" />;
  }
};

const NotificationCard = ({ notification, onMarkRead }) => {
  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Adesso';
    if (diffMins < 60) return `${diffMins} min fa`;
    if (diffHours < 24) return `${diffHours} ore fa`;
    if (diffDays < 7) return `${diffDays} giorni fa`;
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      layout
    >
      <Card 
        className={`hover-card transition-all ${notification.read ? 'opacity-60' : 'border-l-4 border-l-primary'}`}
        data-testid={`notification-${notification.id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
              notification.type === 'safety_alert' ? 'bg-rose-100' : 'bg-amber-100'
            }`}>
              <NotificationIcon type={notification.type} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h3 className={`font-medium ${notification.read ? '' : 'font-semibold'}`}>
                  {notification.title}
                </h3>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {timeAgo(notification.created_at)}
                  </span>
                  {!notification.read && (
                    <Badge variant="default" className="text-xs">Nuovo</Badge>
                  )}
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-1">{notification.message}</p>
              {!notification.read && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 h-7 text-xs"
                  onClick={() => onMarkRead(notification.id)}
                  data-testid={`mark-read-${notification.id}`}
                >
                  <Check className="w-3 h-3 mr-1" />
                  Segna come letto
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = async () => {
    try {
      const response = await notificationsApi.getAll();
      setNotifications(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle notifiche');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleMarkRead = async (id) => {
    try {
      await notificationsApi.markRead(id);
      setNotifications(notifications.map(n => 
        n.id === id ? { ...n, read: true } : n
      ));
    } catch (error) {
      toast.error('Errore durante l\'aggiornamento');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      toast.success('Tutte le notifiche segnate come lette');
    } catch (error) {
      toast.error('Errore durante l\'aggiornamento');
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div data-testid="notifications-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div className="page-header mb-0">
          <h1 className="page-title">Notifiche</h1>
          <p className="page-subtitle">
            {unreadCount > 0 
              ? `${unreadCount} notifiche non lette`
              : 'Nessuna notifica non letta'
            }
          </p>
        </div>
        {unreadCount > 0 && (
          <Button 
            variant="outline" 
            onClick={handleMarkAllRead}
            data-testid="mark-all-read-btn"
          >
            <CheckCheck className="w-4 h-4 mr-2" />
            Segna tutte come lette
          </Button>
        )}
      </div>

      {notifications.length > 0 ? (
        <div className="space-y-3">
          {notifications.map((notification) => (
            <NotificationCard
              key={notification.id}
              notification={notification}
              onMarkRead={handleMarkRead}
            />
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <Bell className="empty-state-icon" />
          <p className="empty-state-title">Nessuna notifica</p>
          <p className="empty-state-description">
            Le notifiche relative a scadenze documenti e formazione appariranno qui
          </p>
        </div>
      )}

      {/* Info Card */}
      <Card className="mt-8 bg-secondary/30">
        <CardContent className="p-6">
          <h3 className="font-heading font-bold text-lg mb-3">Come funzionano le notifiche</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-amber-600" />
              <span>Ricevi avvisi per documenti in scadenza (certificati, planimetrie, ecc.)</span>
            </li>
            <li className="flex items-center gap-2">
              <GraduationCap className="w-4 h-4 text-amber-600" />
              <span>Ricevi avvisi per attestati formativi in scadenza</span>
            </li>
            <li className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-600" />
              <span>Ricevi alert per controlli sicurezza che richiedono intervento</span>
            </li>
          </ul>
          <p className="text-xs text-muted-foreground mt-4">
            Le notifiche vengono inviate automaticamente in base ai giorni di promemoria impostati per ogni documento/formazione.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
